<template>
  <div class="lc-repayment-view" v-loading="loading">
    <div class="page-header">
      <div class="header-left">
        <h2>LC 상환 스케줄</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/lc-repayment'" />
        <span class="sub">미결제 LC를 상환예정일(만기) 순으로 표시 — <b style="color:#67c23a">확정</b>=인수일 기반 · <b style="color:#e6a23c">추정</b>=인수일 미입력(ETA/송장일 기준)</span>
      </div>
      <div class="header-right">
        <el-radio-group v-model="rangeDays" size="small" @change="fetchData">
          <el-radio-button :label="0">전체</el-radio-button>
          <el-radio-button :label="30">30일 이내</el-radio-button>
          <el-radio-button :label="7">7일 이내</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- ag-grid 전환 (2026-07-12) -->
    <div class="ag-theme-alpine repay-grid" style="height: 560px; width: 100%;">
      <ag-grid-vue
        style="width: 100%; height: 100%;"
        :columnDefs="columnDefs"
        :rowData="rows"
        :defaultColDef="defaultColDef"
        @row-clicked="goDetail"
      ></ag-grid-vue>
      <div class="grid-hint">행 클릭 → LC 상세</div>
    </div>
    <el-empty v-if="!loading && !rows.length" description="미결제 LC가 없습니다" :image-size="70" />
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { AgGridVue } from 'ag-grid-vue3'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import axiosInstance from '@/api/axios'

const router = useRouter()
const loading = ref(false)
const rows = ref([])
const rangeDays = ref(0)

const fmt = (v) => (v == null ? '0' : Number(v).toLocaleString())

const defaultColDef = { resizable: true, sortable: true, filter: true, floatingFilter: true, minWidth: 90 }
const columnDefs = ref([
  { headerName: 'LC번호', field: 'lc_number', width: 150, filter: 'agTextColumnFilter', pinned: 'left' },
  { headerName: '수입송장', field: 'invoice_number', width: 140, filter: 'agTextColumnFilter' },
  { headerName: '법인', field: 'company_name', width: 120 },
  { headerName: '통화', field: 'currency', width: 80 },
  {
    headerName: '상환금액', width: 140, cellClass: 'num-cell', filter: 'agNumberColumnFilter',
    valueGetter: (p) => Number(p.data?.fx_amount || p.data?.total_amount || 0),
    valueFormatter: (p) => fmt(p.value),
  },
  // 확정/추정 구분 (2026-07-21, 김기명 제보): 인수일 기반이면 확정,
  // 인수일 없이 ETA·송장일로 계산된 만기는 추정 — 주황으로 구분 표시
  { headerName: '상환예정일(만기)', field: 'due_date', width: 175, sort: 'asc',
    valueFormatter: (p) => {
      if (!p.value) return ''
      return p.data?.is_confirmed ? `${p.value} ✓확정` : `${p.value} (${p.data?.due_basis || '추정'})`
    },
    cellClassRules: {
      'due-confirmed': (p) => !!p.data?.is_confirmed,
      'due-estimated': (p) => !p.data?.is_confirmed,
    },
    tooltipValueGetter: (p) => (p.data?.is_confirmed
      ? '인수일 기반 확정 만기'
      : '추정 만기 — 수입송장에 인수일을 입력하면 확정됩니다'),
  },
  {
    headerName: 'D-day', field: 'days_until', width: 95, filter: 'agNumberColumnFilter',
    valueFormatter: (p) => (p.value == null ? '-' : (p.value < 0 ? `D+${Math.abs(p.value)}` : `D-${p.value}`)),
    cellClassRules: {
      'dday-over': (p) => p.value != null && p.value < 0,
      'dday-soon': (p) => p.value != null && p.value >= 0 && p.value <= 7,
    },
  },
  { headerName: '상태', field: 'status_display', width: 105 },
  { headerName: '지급번호', field: 'payment_number', width: 160, filter: 'agTextColumnFilter' },
])

const fetchData = async () => {
  loading.value = true
  try {
    const params = {}
    if (rangeDays.value) params.days = rangeDays.value
    const res = await axiosInstance.get('lcs/repayment_schedule/', { params })
    rows.value = res.data || []
  } catch (e) {
    console.error('Failed to fetch repayment schedule:', e)
    ElMessage.error('상환 스케줄을 불러오지 못했습니다.')
  } finally {
    loading.value = false
  }
}

const goDetail = (e) => {
  if (e.data?.lc_id) router.push(`/lcs/${e.data.lc_id}`)
}

onMounted(fetchData)
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.header-left h2 { margin: 0; display: inline-block; }
.header-left .sub { margin-left: 10px; color: #909399; font-size: 0.85rem; }

.repay-grid {
  margin-bottom: 26px;
}

.repay-grid :deep(.ag-row) {
  cursor: pointer;
}

.grid-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

:deep(.num-cell) {
  text-align: right;
}

:deep(.dday-soon) {
  color: #e6a23c;
  font-weight: 600;
}

:deep(.dday-over) {
  color: #f56c6c;
  font-weight: 600;
}

:deep(.due-confirmed) {
  color: #67c23a;
}

:deep(.due-estimated) {
  color: #e6a23c;
}
</style>
